import React, { useState, useEffect } from "react";
import { supabase } from "../../lib/supabase";

const AdminAuth = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check current auth session
    const checkAuthSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Error getting session:", error);
          setLoading(false);
          return;
        }

        if (session?.user) {
          // Verify user has admin role
          const isAdmin = await verifyAdminRole(session.user);
          if (isAdmin) {
            setIsAuthenticated(true);
            setUser(session.user);
          } else {
            setError("Access denied. Admin privileges required.");
            await supabase.auth.signOut();
          }
        }
      } catch (error) {
        console.error("Session check error:", error);
      } finally {
        setLoading(false);
      }
    };

    checkAuthSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          const isAdmin = await verifyAdminRole(session.user);
          if (isAdmin) {
            setIsAuthenticated(true);
            setUser(session.user);
            setError("");
          } else {
            setError("Access denied. Admin privileges required.");
            await supabase.auth.signOut();
          }
        } else if (event === 'SIGNED_OUT') {
          setIsAuthenticated(false);
          setUser(null);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const verifyAdminRole = async (user) => {
    try {
      // Check if user exists in admin_users table with admin role
      const { data, error } = await supabase
        .from('admin_users')
        .select('role, is_active')
        .eq('email', user.email)
        .eq('is_active', true)
        .single();

      if (error || !data) {
        console.log("User not found in admin_users table:", user.email);
        return false;
      }

      return data.role === 'admin' || data.role === 'super_admin';
    } catch (error) {
      console.error("Error verifying admin role:", error);
      return false;
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password,
      });

      if (error) {
        setError(error.message);
        return;
      }

      // Authentication successful - verifyAdminRole will be called by onAuthStateChange
    } catch (error) {
      setError("An unexpected error occurred. Please try again.");
      console.error("Login error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      setIsAuthenticated(false);
      setUser(null);
      setEmail("");
      setPassword("");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="admin-login-container">
        <div className="login-card">
          <div className="login-header">
            <h2>Panou Administrator</h2>
            <p>Autentifică-te pentru a accesa panoul de administrare</p>
          </div>

          <form onSubmit={handleLogin} className="login-form">
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            <div className="mb-3">
              <label htmlFor="email" className="form-label">
                Email
              </label>
              <input
                type="email"
                className="form-control"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div className="mb-3">
              <label htmlFor="password" className="form-label">
                Parolā
              </label>
              <input
                type="password"
                className="form-control"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <button 
              type="submit" 
              className="btn btn-primary w-100" 
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Logging in...
                </>
              ) : (
                'Login'
              )}
            </button>
          </form>
        </div>

        <style>{`
          .admin-login-container {
            min-height: 100vh;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px;
          }
          
          .login-card {
            background: white;
            border-radius: 10px;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
            padding: 40px;
            width: 100%;
            max-width: 400px;
          }
          
          .login-header {
            text-align: center;
            margin-bottom: 30px;
          }
          
          .login-header h2 {
            color: #333;
            margin-bottom: 10px;
          }
          
          .login-header p {
            color: #666;
            margin: 0;
          }
          
          .login-form .form-control {
            padding: 12px;
            border-radius: 6px;
          }
          
          .login-form .btn {
            padding: 12px;
            border-radius: 6px;
            font-weight: 500;
          }
          
          .login-footer {
            text-align: center;
            margin-top: 20px;
            padding-top: 20px;
            border-top: 1px solid #eee;
          }
        `}</style>
      </div>
    );
  }

  // If authenticated, render children with logout option and user info
  return (
    <div>
      {React.cloneElement(children, { 
        onLogout: handleLogout,
        user: user 
      })}
    </div>
  );
};

export default AdminAuth;
